import { ChatMessage, MessageSender, MessageType } from '../ChatMessages'
import {
  getAgentResponse,
  getErrorMessages,
  getModelOrFallback,
  getRecommendations,
  getSuggestions,
} from './helpers'
import { ChatResponsePayload } from './types'

enum RestoredMessageRole {
  User = 'user',
  Assistant = 'assistant',
}

export const getAgentMessages = (payload: unknown): ChatMessage[] => {
  const messages: ChatMessage[] = []
  const responsePayload = (payload || {}) as ChatResponsePayload

  const errorMessages = getErrorMessages(responsePayload)
  if (errorMessages.length) {
    return errorMessages
  }

  const { fallbackMessages, model } = getModelOrFallback(payload)

  if (!model) {
    return fallbackMessages
  }

  const recommendations = getRecommendations(model.recommendations)
  const agentResponse = getAgentResponse(model)

  if (agentResponse.length) {
    messages.push(...agentResponse)
  }

  if (recommendations.length) {
    const giftRecommendations = recommendations.filter(
      recommendation => recommendation.productType === 'gifts',
    )
    const cardRecommendations = recommendations.filter(
      recommendation => recommendation.productType === 'cards',
    )

    if (giftRecommendations.length) {
      messages.push({
        sender: MessageSender.Agent,
        type: MessageType.Products,
        content: {
          title: 'Recommended Gifts', // get proper title from agent
          items: giftRecommendations,
        },
      })
    }

    if (cardRecommendations.length) {
      messages.push({
        sender: MessageSender.Agent,
        type: MessageType.Products,
        content: {
          title: 'Recommended Cards', // get proper title from agent
          items: cardRecommendations,
        },
      })
    }
  }

  const suggestions = getSuggestions(model)
  if (suggestions.length) {
    messages.push(...suggestions)
  }

  return messages
}

const getRestoredMessage = (message: unknown): ChatMessage[] => {
  if (!message || typeof message !== 'object' || Array.isArray(message)) {
    return []
  }

  const { role, content } = message as Record<string, unknown>

  if (role === RestoredMessageRole.User) {
    const userContent = String(content || '').trim()

    return userContent
      ? [
          {
            sender: MessageSender.User,
            type: MessageType.Text,
            content: userContent,
          },
        ]
      : []
  }

  if (role === RestoredMessageRole.Assistant) {
    return getAgentMessages({
      agentResponse: {
        response: content,
      },
    })
  }

  return []
}

export const getRestoredMessages = (payload: unknown): ChatMessage[] => {
  const responsePayload = (payload || {}) as ChatResponsePayload

  if (!Array.isArray(responsePayload.messages)) {
    return []
  }

  return responsePayload.messages.flatMap(getRestoredMessage)
}
